import { supabase } from '../supabase';
import type { User } from '../../types/auth';
import type { Member } from '../../types/member';

export async function getUsersByRole(role?: 'user' | 'admin'): Promise<User[]> {
  try {
    const { data: authUsers, error: authError } = await supabase
      .from('auth_users')
      .select('*');
    
    if (authError) {
      throw authError;
    }
    
    // Get user metadata from auth.users
    const { data:{users}, error: usersError } = await supabase.auth.admin.listUsers();
    
    if (usersError) {
      throw usersError;
    }
    
    // Combine data from both sources
    const combinedUsers = users.map(user => ({
        id: user.id,
        email: user.email || '',
        role: user.user_metadata?.role || 'user',
        createdAt: user.created_at,
        updatedAt: user.updated_at,
        lastLoginAt: user.last_sign_in_at || user.created_at,
        isVerified: user.email_confirmed_at !== null
      })) as User[];
    
    if (role) {
      return combinedUsers.filter(user => user.role === role);
    }
    
    return combinedUsers;
  } catch (error) {
    console.error('Failed to get users:', error);
    return [];
  }
}

export const decrtyptReferralCode = (code: string): string => {
  // Placeholder for actual encryption logic
  const decrypted = decodeURIComponent(atob(code));
  // Remove timestamp and return the name part
  const parts = decrypted.split('_');
  if (parts.length < 2) return decrypted; // If no timestamp, return as is
  return parts.slice(0, -1).join('_'); // Join all parts except
};


export async function getUserCount(): Promise<number> {
  try {
    const { count, error } = await supabase
      .from('users')
      .select('*', { count: 'exact', head: true });
    
    if (error) {
      throw error;
    }
    
    return count || 0;
  } catch (error) {
    console.error('Failed to get user count:', error);
    return 0;
  }
}

export async function deleteUser(userId: string): Promise<boolean> {
  try {
    // Delete from auth.users will cascade to users table
    const { error } = await supabase.auth.admin.deleteUser(userId);
    
    if (error) {
      throw error;
    }
    
    return true;
  } catch (error) {
    console.error('Failed to delete user:', error);
    return false;
  }
}

export async function updateUserRole(userId: string, role: 'user' | 'admin'): Promise<boolean> {
  try {
    const { error } = await supabase.auth.admin.updateUserById(userId, {
      user_metadata: { role }
    });
    
    if (error) {
      throw error;
    }
    
    return true;
  } catch (error) {
    console.error('Failed to update user role:', error);
    return false;
  }
}